import { UserAddOutlined } from '@ant-design/icons';
import { AuthRoles } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { BasketballGame, SelectOption, UserBasic } from '@hefe/models';
import {
  BasketballGamesSlice,
  BasketballRefereesSlice,
  currentUserAndChildUsersRefereesSelectSelctor,
  currentUserSelector,
} from '@hefe/store';
import { fullUserName, userHasRole } from '@hefe/utils';
import { Button, Popconfirm, Select, Spin, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import InlineEditInput from '../../../../core/components/inline-edit-input/inline-edit-input.component';

interface GameDetailsUpdateRefereeComponentProps {
  game: BasketballGame;
  refereeNumber: number;
}

export default function GameDetailsUpdateRefereeComponent(
  props: GameDetailsUpdateRefereeComponentProps
) {
  const { game, refereeNumber } = props;

  const dispatch = useAppDispatch();

  const user = useSelector(currentUserSelector);
  const userAndChildUserReferes = useSelector(
    currentUserAndChildUsersRefereesSelectSelctor
  );
  const refereesSelect = useSelector(
    BasketballRefereesSlice.basketballRefereesSelectWithPendingSelector
  );

  const [loadingReferees, setLoadingReferees] = useState(false);
  const [savingReferee, setSavingReferee] = useState(false);
  const [fetchedReferees, setFetchedReferees] = useState(false);

  const fetchReferees = () => {
    if (fetchedReferees) {
      return;
    }

    if (userHasRole(AuthRoles.bbadmin, user)) {
      setLoadingReferees(true);
      dispatch(
        BasketballRefereesSlice.fetchBasketballRefereesSelect({
          cacheDuration: 5 * 60 * 1000,
        })
      )
        .unwrap()
        .then(() => {
          setFetchedReferees(true);
        })
        .catch(() => undefined)
        .finally(() => {
          setLoadingReferees(false);
        });
    }
  };

  const updateReferee = (value: number) => {
    setSavingReferee(true);

    dispatch(
      BasketballGamesSlice.setBasketballGameOwnReferee({
        gameId: game.id,
        data: {
          referee: refereeNumber,
          user_id: value,
        },
      })
    )
      .unwrap()
      .finally(() => {
        setSavingReferee(false);
      });
  };

  const onEnterRefereeEditMode = (editMode: boolean) => {
    if (editMode) {
      fetchReferees();
    }
  };

  const refereeSelects = useMemo(() => {
    if (userHasRole(AuthRoles.bbadmin, user)) {
      return refereesSelect;
    } else {
      return userAndChildUserReferes;
    }
  }, [refereesSelect, user, userAndChildUserReferes]);

  const value = useMemo(() => {
    if (!game.club_details?.own_referees) {
      return undefined;
    }

    const index = `referee${refereeNumber}`;
    const refereeId = (game.club_details as any)?.[index]?.id;

    return refereeId;
  }, [game.club_details, refereeNumber]);

  const text = useMemo(() => {
    if (!game?.club_details?.own_referees) {
      const refereeName = (game as any)?.[`referee_${refereeNumber}`] as string;
      return refereeName ?? t('pending');
    }

    const index = `referee${refereeNumber}`;

    const referee = (game?.club_details as any)?.[index] as UserBasic;

    if (referee) {
      return fullUserName(referee);
    } else {
      return t('pending');
    }
  }, [game, refereeNumber]);

  /*************************************************
   *
   *    MAIN RENDER
   *
   *************************************************/

  if (!userHasRole(AuthRoles.bbadmin, user)) {
    if (savingReferee) {
      return <Spin />;
    }
    const showChildUsers = user?.user_settings?.show_child_users;
    const ownUserCount = userAndChildUserReferes?.length;

    // Return text if no own referees
    if (ownUserCount === 0) {
      return <Typography.Text>{text}</Typography.Text>;
    }

    let otherRefereeId = game?.club_details?.own_referee2_id;
    if (refereeNumber === 2) {
      otherRefereeId = game?.club_details?.own_referee1_id;
    }

    if (
      value ||
      (ownUserCount === 1 &&
        otherRefereeId === userAndChildUserReferes[0].value)
    ) {
      return <Typography.Text>{text}</Typography.Text>;
    }

    // Show "assign me" button instead of select
    if (!showChildUsers || ownUserCount < 2) {
      return (
        <Popconfirm
          title={t('referees.assign_me.description')}
          onConfirm={() =>
            updateReferee(userAndChildUserReferes[0].value as number)
          }
          overlayStyle={{ maxWidth: 400 }}
        >
          <Button style={{ padding: 0 }} icon={<UserAddOutlined />} type="link">
            {t('referees.assign_me.title')}
          </Button>
        </Popconfirm>
      );
    }
  }

  return (
    <InlineEditInput
      disabled={!game?.club_details?.own_referees || savingReferee}
      highlightingMode="self"
      component={Select}
      value={value}
      text={text}
      loading={savingReferee || loadingReferees}
      onOk={(value) => updateReferee(value)}
      onModeChange={(editMode) => onEnterRefereeEditMode(editMode)}
      componentProps={{
        options: refereeSelects,
        allowClear: userHasRole(AuthRoles.bbadmin, user),
        showSearch: true,
        filterOption: (input: string, option: SelectOption) =>
          option?.label?.toLowerCase().includes(input?.toLowerCase()),
      }}
    />
  );
}
